import {useEffect, useRef} from "react";
import {Button, Form, Input, Switch} from "antd";
import HospitalSelectBox from "../../../HospitalSelectBox";
import {t} from 'i18next'
import AckRuleEditor from "../../../../hl7/ack-rules/AckRuleEditor";
import {parseRules, serializeRules} from "../../../../hl7/ack-rules/ackRuleUtils";

const splitMappings = (text) => (text || '').split(',').map(x => x.trim()).filter(Boolean);

const ADDRESS_EXAMPLE = '10.20.30.40:6000';

const REVISION_EXAMPLE = 'OBX-4=1;OBX-4=2';
const REVISION_ENTRY = /^\s*[A-Za-z][A-Za-z0-9]{2}\s*-\s*\d{1,3}\s*=/;

// Backend ile AYNI kural (ReportRevisionFields.Validate): tek satır, ';' ile ayrılmış EN FAZLA
// iki "SEG-FIELD=DEĞER" ataması. Soldaki ilk gönderimde, sağdaki rapor düzenlendikten sonraki
// gönderimlerde uygulanır. Boş taraf serbest — o gönderimde mesaja dokunulmaz.
// Bozuk bir değer gönderimi patlatmaz, SESSİZCE devre dışı kalır; hastane düzeltme kodunu
// almadığını fark etmez. Bu yüzden hem burada hem sunucuda kaydetmeye izin verilmiyor.
const validateRevisionFields = (_, value) => {
    if (!value || !value.trim())
        return Promise.resolve();

    const parts = value.split(';');

    if (parts.length > 2)
        return Promise.reject(new Error(t('Report revision fields too many')));

    const invalid = parts.filter(p => p.trim() && !REVISION_ENTRY.test(p));

    return invalid.length
        ? Promise.reject(new Error(`${t('Invalid report revision fields')}: ${invalid.map(x => x.trim()).join(', ')}`))
        : Promise.resolve();
};

// Backend ile AYNI kural (HL7ClientService.NormalizeAddress): boş serbest ("yapılandırılmamış"),
// dolu ise tek ':' ve 1-65535 portu. Rapor adresi gönderim yolunda Split(":")[1] + int.Parse ile
// okunuyor ve StudyReportableService bunu try/catch DIŞINDA yapıyor — bozuk bir değer orada
// kayıt başına exception demek. Sunucu da aynı kontrolü yapar; bu, hatayı forma taşımak içindir.
const validateAddress = (_, value) => {
    if (!value || !value.trim())
        return Promise.resolve();

    const parts = value.trim().split(':');
    const port = parts.length === 2 ? Number(parts[1].trim()) : NaN;

    const valid = parts.length === 2
        && parts[0].trim().length > 0
        && /^\d+$/.test(parts[1].trim())
        && port >= 1
        && port <= 65535;

    return valid
        ? Promise.resolve()
        : Promise.reject(new Error(`${t('Address must be ip port')} (${ADDRESS_EXAMPLE})`));
};

export default function HL7ClientCreateEditModal({hospitalId, data, onSave}) {
    const formRef = useRef()

    useEffect(() => {
        if(data) {
            formRef.current.setFieldsValue({
                ...data,
                name: data?.appClient?.name,
                modalityMappingsText: (data?.modalityMappings || []).join(', '),
                ackRuleItems: parseRules(data?.ackRules)
            })
        } else {
            const date = new Date();
            formRef.current.setFieldsValue({
                name: `hl7-client-${date.getTime()}`,
                // Sunucudaki varsayılanla aynı (ReportRevisionFields.Default): yeni istemci de
                // düzeltilmiş raporu işaretleyerek gönderir. İstenmiyorsa alan boşaltılır.
                reportRevisionFields: REVISION_EXAMPLE
            })
        }
    }, [data]);

    useEffect(() => {
        if(hospitalId) {
            formRef.current.setFieldsValue({
                hospitalId: hospitalId
            })
        }
    }, [hospitalId])

    const finish = async () => {
        const values = formRef.current.getFieldsValue();

        values.ackRules = serializeRules(values.ackRuleItems);
        delete values.ackRuleItems;

        values.modalityMappings = splitMappings(values.modalityMappingsText);
        delete values.modalityMappingsText;

        onSave && onSave(values);
    }

    return (
        <>
            <Form
                layout={'vertical'}
                ref={formRef}
                onFinish={finish}
            >
                <Form.Item
                    label={t('Hospital')}
                    name={'hospitalId'}
                >
                    <HospitalSelectBox mode={'single'} disabled={true} />
                </Form.Item>

                <Form.Item
                    label={t('Name')}
                    name={'name'}
                >
                    <Input disabled={data} />
                </Form.Item>

                <Form.Item
                    label={t('Description')}
                    name={'description'}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label={t('Enabled')}
                    name={'enabled'}
                    valuePropName={'checked'}
                >
                    <Switch />
                </Form.Item>

                {/* İstem BURAYA gelir: docker'daki HL7 dinleyicisinin adresi. Elle girilir ve
                    yalnızca bilgi amaçlıdır — dinleyici portunu kendi appsettings.json'ından okur. */}
                <Form.Item
                    label={t('HL7 Inbound Address')}
                    name={'inboundAddress'}
                    tooltip={t('HL7 Inbound Address Description')}
                    rules={[{validator: validateAddress}]}
                >
                    <Input placeholder={ADDRESS_EXAMPLE} allowClear />
                </Form.Item>

                {/* Rapor BURAYA gider. Canlı teslimatı etkiler; format doğrulaması şart. */}
                <Form.Item
                    label={t('HL7 Report Address')}
                    name={'reportAddress'}
                    tooltip={t('HL7 Report Address Description')}
                    rules={[{validator: validateAddress}]}
                >
                    <Input placeholder={ADDRESS_EXAMPLE} allowClear />
                </Form.Item>

                {/* İlk rapor / düzeltilmiş rapor ayrımı. HBYS'ler bunu farklı alanlarda ve farklı
                    kodlarla bekliyor, bu yüzden istemci bazlı: aynı hastanenin iki kurumu bile
                    farklı değer taşıyabilir. Boş bırakılırsa mesaja hiç dokunulmaz. */}
                <Form.Item
                    label={t('Report Revision Fields')}
                    name={'reportRevisionFields'}
                    tooltip={t('Report Revision Fields Description')}
                    rules={[{validator: validateRevisionFields}]}
                >
                    <Input placeholder={REVISION_EXAMPLE} allowClear />
                </Form.Item>

                <Form.Item
                    label={t('Modality Mapping')}
                    name={'modalityMappingsText'}
                    tooltip={t('Modality Mapping Description')}
                    rules={[{
                        validator: (_, value) => {
                            const invalid = splitMappings(value).filter(x => !/^[^:\s]+:[^:\s]+$/.test(x))
                            return invalid.length
                                ? Promise.reject(new Error(`${t('Invalid modality mapping')}: ${invalid.join(', ')}`))
                                : Promise.resolve()
                        }
                    }]}
                >
                    <Input placeholder={'CR:XA, MR:CT'} />
                </Form.Item>

                <Form.Item label={t('ACK Rules')}>
                    <AckRuleEditor name={'ackRuleItems'} />
                </Form.Item>

                <Form.Item>
                    <Button htmlType={'submit'} type={'primary'}>
                        {t('Save')}
                    </Button>
                </Form.Item>
            </Form>
        </>
    )
}
